import React from 'react';
import {
  Action,
  Card, Carousel, FontWeight, HStack, Icon, ICON_EMPTY, Spacing, Stack, Text, UseModalResult,
} from '@doveit/bricks';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { MarketingOpportunity, MarketingOpportunityTrigger } from '../../types';
import { MARKETING_OPPORTUNITIES, MARKETING_TRIGGERS } from '../../constants';
import CanvaModelCard from '../canva-model-card/CanvaModelCard';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { buildCanvaFillPath, getMarketingTriggerKey, getTriggerAutofillParams } from '../../utils/autofill/utils';
import { goToPage } from '../../../utils/navigate/utils';

export interface MarketingOpportunitiesModalProps extends React.AriaAttributes, UseModalResult<any> {
  triggers: MarketingOpportunityTrigger[],
}

const MarketingOpportunitiesModal: React.FC<MarketingOpportunitiesModalProps> = ({
  triggers,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');
  const [selectedTrigger, setSelectedTrigger] = React.useState<MarketingOpportunityTrigger>();
  const [selectedOpportunity, setSelectedOpportunity] = React.useState<MarketingOpportunity>();

  React.useEffect(() => {
    if (triggers.length === 1) {
      setSelectedTrigger(triggers[0]);
    }
  }, [triggers]);

  const autofillParams = React.useMemo(
    () => getTriggerAutofillParams(selectedTrigger),
    [selectedTrigger],
  );

  const handleSelectOpportunity = React.useCallback((opportunity: MarketingOpportunity) => {
    const availableTemplates = MARKETING_OPPORTUNITIES[opportunity].templates;
    if (availableTemplates.length === 1) {
      goToPage(buildCanvaFillPath(availableTemplates[0], autofillParams));
    } else {
      setSelectedOpportunity(opportunity);
    }
  }, [autofillParams]);

  const handleGoBack = React.useCallback(() => {
    if (selectedOpportunity) {
      setSelectedOpportunity(undefined);
    } else if (selectedTrigger) {
      setSelectedTrigger(undefined);
    }
  }, [selectedOpportunity, selectedTrigger]);

  if (triggers.length === 0) {
    return null;
  }

  return (
    <SimpleModal
      {...rest}
      title={selectedOpportunity ? MARKETING_OPPORTUNITIES[selectedOpportunity].title : 'Suggerimenti di marketing'}
      aria-label={selectedOpportunity ? MARKETING_OPPORTUNITIES[selectedOpportunity].title : 'Suggerimenti di marketing'}
      padded={!selectedOpportunity}
      footerContentAlignment="left"
      footer={(selectedOpportunity || (selectedTrigger && triggers.length > 1)) ? (
        <Action
          label="Indietro"
          emphasis="high"
          onClick={handleGoBack}
        />
      ) : undefined}
    >
      {!selectedTrigger && !selectedOpportunity && (
        <Stack gap={300}>
          <Text.Body>Puoi intraprendere delle azioni di marketing a partire dai seguenti eventi:</Text.Body>

          <Stack gap={200}>
            {triggers.map((trigger) => (
              <Card key={getMarketingTriggerKey(trigger)}>
                <Card.Content>
                  <Stack gap={150}>
                    <HStack gap={150} alignItems="center" justifyContent="space-between" wrap="nowrap">
                      <HStack gap={150} alignItems="center" wrap="nowrap">
                        <div>
                          <Icon
                            path={MARKETING_TRIGGERS[trigger.type].icon}
                            size={32}
                          />
                        </div>
                        <Text.Body fontWeight={FontWeight.MEDIUM}>
                          {MARKETING_TRIGGERS[trigger.type].title}
                        </Text.Body>
                      </HStack>

                      {!isMobile && (
                        <Action
                          label="Vedi azioni"
                          size="S"
                          color="primary"
                          onClick={() => setSelectedTrigger(trigger)}
                          width="9rem"
                        />
                      )}
                    </HStack>

                    {isMobile && (
                      <HStack gap={150}>
                        <div>
                          <Icon path={ICON_EMPTY} size={32} />
                        </div>

                        <Action
                          label="Vedi azioni"
                          size="S"
                          color="primary"
                          onClick={() => setSelectedTrigger(trigger)}
                          width="9rem"
                        />
                      </HStack>
                    )}
                  </Stack>
                </Card.Content>
              </Card>
            ))}
          </Stack>
        </Stack>
      )}

      {selectedTrigger && !selectedOpportunity && (
        <Stack gap={300}>
          <Stack gap={200}>
            <Text.Body
              fontWeight={FontWeight.MEDIUM}
              color="primary.default.low"
            >
              {MARKETING_TRIGGERS[selectedTrigger.type].title}
            </Text.Body>
          </Stack>

          <Stack gap={200}>
            {MARKETING_TRIGGERS[selectedTrigger.type].opportunities.map((opportunity) => (
              <Card key={opportunity}>
                <Card.Content>
                  <Stack gap={150}>
                    <HStack gap={150} alignItems="center" justifyContent="space-between" wrap="nowrap">
                      <HStack gap={150} alignItems={isMobile ? 'start' : 'center'} wrap="nowrap">
                        <div>
                          <Icon
                            path={MARKETING_OPPORTUNITIES[opportunity].icon}
                            size={32}
                          />
                        </div>

                        <Text.Body>
                          <strong>{MARKETING_OPPORTUNITIES[opportunity].title}</strong>: {MARKETING_OPPORTUNITIES[opportunity].description}
                        </Text.Body>
                      </HStack>

                      {!isMobile && (
                        <Action
                          label="Usa template"
                          aria-label={`Usa template per ${MARKETING_OPPORTUNITIES[opportunity].title}`}
                          size="S"
                          color="primary"
                          onClick={() => handleSelectOpportunity(opportunity)}
                          width="9rem"
                        />
                      )}
                    </HStack>

                    {isMobile && (
                      <HStack gap={150}>
                        <div>
                          <Icon path={ICON_EMPTY} size={32} />
                        </div>
                        <Action
                          label="Usa template"
                          aria-label={`Usa template per ${MARKETING_OPPORTUNITIES[opportunity].title}`}
                          size="S"
                          color="primary"
                          onClick={() => handleSelectOpportunity(opportunity)}
                          width="9rem"
                        />
                      </HStack>
                    )}
                  </Stack>
                </Card.Content>
              </Card>
            ))}
          </Stack>
        </Stack>
      )}

      {selectedOpportunity && (
        <Stack gap={300} wrap="nowrap">
          <Spacing margin={[300, 300, 0]}>
            <Text.Body>Puoi scegliere di generare il tuo design a partire da uno dei seguenti template:</Text.Body>
          </Spacing>

          <Spacing padding={[0, 0, 300, 300]}>
            <Carousel
              totalElements={MARKETING_OPPORTUNITIES[selectedOpportunity].templates.length}
              gap={300}
              itemWidth={220}
            >
              <div style={{ overflow: 'hidden' }}>
                <Carousel.Items>
                  {MARKETING_OPPORTUNITIES[selectedOpportunity].templates.map((brandTemplateId) => (
                    <Carousel.Item key={brandTemplateId}>
                      <CanvaModelCard
                        brandTemplateId={brandTemplateId}
                        autofillParams={autofillParams}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel.Items>
              </div>
            </Carousel>
          </Spacing>
        </Stack>
      )}
    </SimpleModal>
  );
};

export default MarketingOpportunitiesModal;
